// ** React Imports
import React, { createContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// ** MUI Imports
import Box from '@mui/material/Box'

// ** Third party libraries
import Cookies from 'js-cookie'

// ** SPINNER
import FallbackSpinner from 'core/components/spinner'
import {
  getAdminAccess,
  getAdminAppAccess,
  refreshFreshbooksToken,
  validateAdminToken,
  getPricingAccessCheck
} from 'configs/axios/api_helper'

// ** Config
// import authConfig from 'src/configs/auth'

// ** Defaults
const defaultProvider = {
  user: null,
  loading: true,
  setUser: () => null,
  setLoading: () => Boolean,
  error: false,
  isInitialized: false,
  login: () => null,
  logout: () => null,
  setIsInitialized: () => Boolean,
  register: () => Promise.resolve(),
  pricingAccess: false
}
const AuthContext = createContext(defaultProvider)

const AuthProvider = ({ children }) => {
  // ** States
  const [user, setUser] = useState(defaultProvider.user)
  const [loading, setLoading] = useState(defaultProvider.loading)
  const [error, setError] = useState(defaultProvider.error)
  const [isInitialized, setIsInitialized] = useState(defaultProvider.isInitialized)
  const [pricingAccess, setPricingAccess] = useState(defaultProvider.pricingAccess)

  // ** Hooks
  const navigate = useNavigate()

  // ** This part needs to be refactored for securtiy issues (IMPORTANT!!!)
  const search = window.location.search
  const params = new URLSearchParams(search)
  const sharedEmail = params.get('email')
  const sharedName = params.get('name')
  const sharedPicture = params.get('picture')

  useEffect(() => {
    const initAuth = async () => {
      setIsInitialized(true)
      setLoading(true)
      const storedToken = Cookies.get('jwt')
      if (storedToken !== undefined && storedToken != 'undefined') {
        const tokenInfo = await validateAdminToken()
        setLoading(false)
        const isValidToken = tokenInfo?.status === 200 && tokenInfo?.data?.auth
        if (!isValidToken) {
          Cookies.remove('userData')
          Cookies.remove('jwt')
          setUser(null)
          navigate('/login')
        } else {
          const storedUser = Cookies.get('userData')
          setUser(JSON.parse(storedUser) ?? null)
          const accessCheck = await getPricingAccessCheck()
          setPricingAccess(!!accessCheck?.data?.hasAccess)
          if (window.location.pathname == '/login') navigate('/')
          else navigate(window.location.pathname)
        }
      } else {
        setLoading(false)
      }
    }
    initAuth()
  }, [])

  const handleLogin = async (data, errorCallback) => {
    // setLoading(true)
    setError(false)
    Cookies.remove('userData')
    Cookies.remove('jwt')
    try {
      // Send the Google access token, NOT the email. The backend verifies the
      // token with Google and derives the trusted email itself, so a claimed
      // email can no longer be used to log in as someone else.
      const response = await getAdminAccess({ access_token: data.access_token })
      if (response.token) {
        Cookies.set('jwt', response.token)
      }
      // Use the backend-verified email for everything downstream.
      const email = response.email || data.email
      const access_apps = await getAdminAppAccess({ email })
      Cookies.set('sharedToken', data.access_token)
      Cookies.set(
        'userData',
        JSON.stringify({ email, name: data.name, picture: data.picture, access_apps: access_apps })
      )
      setUser({ email, name: data.name, picture: data.picture, access_apps: access_apps })
      const accessCheck = await getPricingAccessCheck()
      setPricingAccess(!!accessCheck?.data?.hasAccess)
      //Call this api to refresh freshbook token in the backend
      //(nothing to do with the front application just an action needs to be executed)
      await refreshFreshbooksToken()
      setLoading(false)
      setLoading(false)
      navigate('/')
    } catch (error) {
      setLoading(false)
      errorCallback({ message: 'not authorized', code: 401 })
      setError(true)
    }
  }

  const handleLogout = () => {
    setUser(null)
    setIsInitialized(false)
    Cookies.remove('userData')
    Cookies.remove('jwt')
    Cookies.remove('accountData')
    navigate('/login')
  }

  const values = {
    user,
    loading,
    error,
    setUser,
    setLoading,
    isInitialized,
    setIsInitialized,
    pricingAccess,
    login: handleLogin,
    logout: handleLogout
    // register: handleRegister
  }
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          mt: '20%'
        }}
      >
        <FallbackSpinner />
      </Box>
    )
  }

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
}

export { AuthContext, AuthProvider }
